import { css, cva } from '@/styled-system/css';

/** Native destination link layered beneath the card's interactive controls. */
export const cardLink = css({
  position: 'absolute',
  inset: 0,
  borderRadius: 'md',
  textDecoration: 'none',
  color: 'inherit',
  cursor: 'pointer',
  zIndex: 'zIndexBase',
  _focusVisible: {
    outlineWidth: 'thick',
    outlineStyle: 'solid',
    outlineColor: 'brand.primary',
    outlineOffset: '50',
  },
});

export const cardForeground = css({
  position: 'relative',
  pointerEvents: 'none',
  zIndex: 'zIndexStep',
  '& :is(a, button, [role="button"])': {
    pointerEvents: 'auto',
  },
});

export const contactActions = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'tighter',
  paddingInline: 'tighter',
  paddingBlockEnd: 'tight',
  width: '100%',
  '& > *': {
    flexBasis: 'calc((100% - {spacing.tighter}) / 2)',
    flexGrow: 0,
    flexShrink: 0,
    minWidth: 0,
  },
  '& > :only-child': {
    flexBasis: '100%',
  },
});

export const messageLabelStyles = css.raw({
  '@container listingCard (min-width: 22em)': {
    fontSize: { base: 'body-sm', lg: 'body' },
  },
});

// Below 22em, the full phone number starts to wrap beside the message action.
export const compactPhoneAction = css({
  display: 'flex',
  flex: 1,
  '@container listingCard (min-width: 22em)': {
    display: { base: 'flex', lg: 'none' },
  },
});

export const fullPhoneAction = css({
  display: 'none',
  flex: 1,
  '@container listingCard (min-width: 22em)': {
    display: { base: 'none', lg: 'flex' },
  },
});

export const photoImageFill = css({
  width: '100%',
  height: '100%',
});

export const listingCardActions = css.raw({
  alignItems: 'center',
  backdropFilter: 'blur(token(sizes.listingCard.actions.backdropBlur))',
  borderRadius: 'pill',
  gap: 'tightest',
  isolation: 'isolate',
  paddingBlock: 'tightest',
  paddingInline: 'tightest',
  // Figma specifies bare-neutral states on the otherwise neutral-fixed image controls.
  '& > :where(a, button)': {
    _hover: {
      backgroundColor: 'bg.action.bare.neutral.hover',
    },
    _active: {
      backgroundColor: 'bg.action.bare.neutral.pressed',
    },
  },
  _before: {
    backgroundColor: 'bg.neutral-fixed',
    borderRadius: 'pill',
    content: '""',
    inset: 0,
    opacity: 'listingCardActionsBackdrop',
    pointerEvents: 'none',
    position: 'absolute',
    zIndex: 'zIndexBase',
  },
});

/** Price line with the total-price / fees indicator inline beside it (`dataArea1`). */
export const priceRow = css({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'tightest',
  minWidth: 0,
  // A little space below the price before the beds/baths line.
  marginBlockEnd: 'tightest',
});

export const priceValue = css({
  textStyle: 'heading-sm',
});

/** Non-interactive "Total price" badge (when no tooltip copy is supplied). */
export const totalPriceBadge = css({
  alignItems: 'center',
  display: 'inline-flex',
  gap: 'tightest',
});

/**
 * Filled circle-check: brand-primary fill with a white stroked check. Lucide
 * hardcodes `fill="none" stroke="currentColor"` as SVG attrs, so override both
 * via CSS (CSS beats those attrs).
 */
export const totalPriceCheckIcon = css({
  color: 'brand.primary',
  fill: 'currentColor',
  flexShrink: 0,
  stroke: 'white',
});

export const totalPriceLabel = css({
  color: 'brand.primary',
  '&&': {
    fontSize: 'body',
    fontWeight: 'bold',
    lineHeight: 'body',
  },
});

export const feesApply = css({
  color: 'text.subtle',
  textStyle: 'body',
});

/** Row wrapper for the beds/baths/units line. */
export const homeDetailsRow = css.raw({
  alignItems: 'center',
  display: 'flex',
  gap: 'tightest',
  '&&&': {
    marginBlockEnd: 'tightest',
  },
});

/**
 * Beds/baths/sqft kept to one line, truncating only at the full card width.
 * Home details are rendered manually (no PropertyCard.HomeDetails) so the ·
 * separator is a literal character in JSX rather than a CSS override.
 */
export const homeDetailsArea = css({
  minWidth: 0,
  overflow: 'hidden',
  textStyle: 'body',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const homeDetailValue = css({
  textStyle: 'body-bold',
});

/** Identity line (`dataArea3`): property-type icon + name/address + building cue. */
export const identityRow = css({
  alignItems: 'center',
  display: 'flex',
  gap: 'tighter',
  minWidth: 0,
  textStyle: 'body',
});

export const identityLabelGroup = css({
  alignItems: 'center',
  display: 'flex',
  gap: 'tightest',
  minWidth: 0,
});

/** City/state/zip appended inline on the identity line, after the building name. */
export const identityAddress = css({
  color: 'text.subtle',
  flexShrink: 0,
  whiteSpace: 'nowrap',
});

export const identityIcon = css({
  color: 'text.subtle',
  flexShrink: 0,
});

export const identityLabel = css({
  color: 'text.subtle',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

/** "View units →" affordance shown when a card opens a multi-unit building page. */
export const buildingCue = css({
  alignItems: 'center',
  color: 'brand.primary',
  display: 'inline-flex',
  fontWeight: 'bold',
  gap: 'micro.default',
  whiteSpace: 'nowrap',
});

/**
 * Surgical overrides for values Constellation hardcodes (not driven by a
 * token, so they can't be themed in `panda.config.ts`).
 *
 * Body bg and DataWrapper horizontal padding are *not* here — both are
 * token-backed and overridden in the HotPads theme:
 *  - `colors.PropertyCard.Body.bg.default` → transparent
 *  - `spacing.PropertyCard.DataWrapper.{large,small}.padding.horz` → 0
 *
 * Photo bottom corners can't be themed: Constellation's photo recipe writes
 * `borderEndStartRadius: '0'` / `borderEndEndRadius: '0'` directly into the
 * `large` / `small` variants, and the carousel-clip rule writes a literal
 * `{radii.PropertyCard.Body} {radii.PropertyCard.Body} 0px 0px` shorthand —
 * no token sits between either of those and the recipe. So we override via
 * a descendant selector on `data-c11n-component`. If Constellation later
 * exposes a `radii.PropertyCard.Photo.{bottomStart,bottomEnd}` token, this
 * block can move to the theme too.
 */
export const cardOverrides = css.raw({
  width: '100%',
  maxWidth: 'none',
  minWidth: 0,
  pointerEvents: 'none',
  '& :is(a, button, [role="button"])': {
    pointerEvents: 'auto',
  },
  '& [data-c11n-component="PropertyCard.PhotoWrapper"], & [data-c11n-component="PropertyCard.DataWrapper"], & [data-c11n-component="PropertyCard.FlexArea"]':
    {
      position: 'relative',
      zIndex: 'zIndexStep',
    },
  '& [data-c11n-component="PropertyCard.PhotoBody"]': {
    borderEndStartRadius: 'obj.default',
    borderEndEndRadius: 'obj.default',
  },
  '& [data-c11n-component="PhotoCarousel.Root"]': {
    borderRadius: 'obj.default',
  },
  '& [data-c11n-component="PropertyCard.PhotoFooter"]': {
    justifySelf: 'flex-start',
    marginBlock: 'tighter',
    marginInline: 'tighter',
  },
  '& [data-c11n-component="PropertyCard.BadgeArea"]': {
    marginBlockStart: '150',
  },
});

export const interactiveCarouselRoot = css.raw({
  pointerEvents: 'auto',
});

const carouselButtonBase = css.raw({
  alignItems: 'center',
  backdropFilter: 'blur({sizes.effect.blur.200})',
  backgroundColor: 'transparent.white-80',
  borderColor: 'transparent.black-15',
  borderRadius: 'ellipse',
  borderStyle: 'solid',
  borderWidth: 'default',
  boxShadow: 'sm',
  color: 'transparent.black-60',
  display: 'flex',
  height: '700',
  justifyContent: 'center',
  minHeight: '700',
  minWidth: '700',
  padding: 0,
  top: '50%',
  transform: 'translateY(-50%)',
  width: '700',
  _active: {
    backgroundColor: 'transparent.white-80',
    boxShadow: 'sm',
  },
  _hover: {
    backgroundColor: 'transparent.white-80',
    boxShadow: 'sm',
    color: 'transparent.black-60',
  },
  '& > [data-c11n-component="Icon"]': {
    filter: 'none',
    height: '400',
    width: '400',
  },
});

const carouselButtonInteractiveState = css.raw({
  backgroundColor: 'transparent.white-80',
  boxShadow: 'sm',
  color: 'transparent.black-60',
});

export const carouselButtonNoHover = css.raw({
  '&&&:active, &&&:hover': carouselButtonInteractiveState,
});

const compactCarouselButtonBase = css.raw({
  alignItems: 'center',
  backdropFilter: 'blur({sizes.effect.blur.200})',
  backgroundColor: 'transparent.white-80',
  borderColor: 'transparent.black-15',
  borderRadius: 'ellipse',
  borderStyle: 'solid',
  borderWidth: 'default',
  boxShadow: 'sm',
  color: 'transparent.black-60',
  display: 'flex',
  height: '600',
  justifyContent: 'center',
  minHeight: '600',
  minWidth: '600',
  padding: 0,
  top: '50%',
  transform: 'translateY(-50%)',
  width: '600',
  _active: {
    backgroundColor: 'transparent.white-80',
    boxShadow: 'sm',
  },
  _hover: {
    backgroundColor: 'transparent.white-80',
    boxShadow: 'sm',
    color: 'transparent.black-60',
  },
  '& > [data-c11n-component="Icon"]': {
    filter: 'none',
    height: '300',
    width: '300',
  },
});

// Hover-zoom on the photo. Constellation exposes no easing/duration tokens, and
// its only transform transition token (`PhotoCarousel.Slide`) uses a
// slide-tuned curve that snaps almost instantly — wrong feel for a zoom. So we
// set an explicit transform transition: ease-out as the pointer enters (set on
// the hover rule), ease-in as it settles back (set on the resting rule). Both
// are gated to `_motionSafe` so reduced-motion users get an instant change.
const SLIDE_ZOOM_TRANSITION = 'transform 300ms';
const slideZoomResting = css.raw({
  transform: 'scale(1)',
  transformOrigin: 'center',
  transition: {
    _motionReduce: 'none',
    _motionSafe: `${SLIDE_ZOOM_TRANSITION} ease-in`,
  },
});
const slideZoomHover = css.raw({
  transform: 'scale(1.05)',
  transition: {
    _motionReduce: 'none',
    _motionSafe: `${SLIDE_ZOOM_TRANSITION} ease-out`,
  },
});

export const cardContainer = css({
  containerName: 'listingCard',
  containerType: 'inline-size',
  position: 'relative',
  isolation: 'isolate',
  width: '100%',
  // Keep card-wide carousel hover local so nested C11n controls own their interaction states.
  _hoverMouse: {
    '&:hover [data-c11n-component="PhotoCarousel.Slide"] img': slideZoomHover,
    '&:hover [data-c11n-component="PhotoCarousel.Root"] > [data-c11n-component="PhotoCarousel.NavControls"]': {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
});

/**
 * Per-appearance geometry: photo aspect ratio + corner radii, carousel
 * affordance (custom nav buttons / dots, mobile-hide + hover-reveal), and the
 * data-area margin resets. Horizontal padding is intentionally not set so the
 * theme's zero `DataWrapper.padding.horz` token applies (transparent data area,
 * flush to the photo edge).
 */
const largeCardOverridesRecipe = cva({
  base: {
    ...cardOverrides,
    '& [data-c11n-component="PropertyCard.Body"]': {
      borderRadius: 'md',
      // Photo / data / flex rows size to content. Constellation's default large
      // grid uses a fixed photo-row height, which the 4:3 aspect-ratio photo
      // overflows — pushing it down over the data area. Auto rows track the
      // real photo height instead.
      gridTemplateRows: 'auto auto auto',
    },
    '& [data-c11n-component="PropertyCard.PhotoWrapper"]': {
      aspectRatio: '4 / 3',
      minBlockSize: 'auto',
      overflow: 'hidden',
      borderRadius: 'md',
    },
    '& [data-c11n-component="PropertyCard.PhotoBody"]': {
      borderRadius: 'md',
    },
    '& [data-c11n-component="PhotoCarousel.Root"]': {
      borderRadius: 'md',
      height: '100%',
    },
    '& [data-c11n-component="PhotoCarousel.Slide"] img': slideZoomResting,
    '& [data-c11n-component="PhotoCarousel.Root"] > [data-c11n-component="PhotoCarousel.NavControls"]': {
      display: 'none',
      opacity: 0,
      pointerEvents: 'none',
      zIndex: 'zIndexStepOver',
      lg: {
        display: 'flex',
      },
    },
    '& [data-c11n-component="PhotoCarousel.PreviousButton"]': {
      ...carouselButtonBase,
      insetInlineEnd: 'auto',
      insetInlineStart: 'tighter',
    },
    '& [data-c11n-component="PhotoCarousel.NextButton"]': {
      ...carouselButtonBase,
      insetInlineEnd: 'tighter',
      insetInlineStart: 'auto',
    },
    '& [data-c11n-component="PhotoCarousel.PreviousButton"]:disabled': {
      opacity: 0,
      pointerEvents: 'none',
    },
    '& [data-c11n-component="PhotoCarousel.NextButton"]:disabled': {
      opacity: 0,
      pointerEvents: 'none',
    },
    '& [data-c11n-component="PhotoCarousel.PreviousButton"]:is(:active, :hover, [data-active], [data-hover]), & [data-c11n-component="PhotoCarousel.NextButton"]:is(:active, :hover, [data-active], [data-hover])':
      carouselButtonInteractiveState,
    '& [data-c11n-component="PhotoCarousel.Dots"]': {
      backgroundColor: 'transparent.invisible',
      gap: '150',
      insetBlockEnd: 'tighter',
      insetInlineEnd: 0,
      insetInlineStart: 0,
      justifyContent: 'center',
      minHeight: 'auto',
      paddingBlock: 0,
      paddingInline: 0,
      transform: 'none',
      width: '100%',
      zIndex: 'zIndexStepOver',
      _focusWithin: {
        backgroundColor: 'transparent.invisible',
      },
    },
    '& [data-c11n-component="PhotoCarousel.Dot"]': {
      backgroundColor: 'transparent.white-40',
      borderRadius: 'ellipse',
      height: '150',
      width: '150',
    },
    '& [data-c11n-component="PhotoCarousel.Dot"][aria-selected="true"]': {
      backgroundColor: 'white',
      height: '150',
      width: '150',
    },
    _hoverMouse: {
      '& [data-c11n-component="PhotoCarousel.Root"]:hover [data-c11n-component="PhotoCarousel.Slide"] img':
        slideZoomHover,
      '& [data-c11n-component="PhotoCarousel.Root"]:hover > [data-c11n-component="PhotoCarousel.NavControls"]': {
        opacity: 1,
        pointerEvents: 'auto',
      },
      '& [data-c11n-component="PhotoCarousel.Root"]:focus-within > [data-c11n-component="PhotoCarousel.NavControls"]': {
        opacity: 1,
        pointerEvents: 'auto',
      },
    },
    '& [data-c11n-component="PropertyCard.DataWrapper"]': {
      marginBlockStart: 0,
      paddingInline: 'tighter',
      paddingBlockStart: 'tight',
      paddingBlockEnd: 'tight',
    },
    '& [data-c11n-component="PropertyCard.FlexArea"]': {
      padding: 0,
    },
    '& [data-c11n-component="PropertyCard.DataArea"]': {
      marginBlockEnd: 0,
      minWidth: 0,
    },
    '& [data-c11n-component="PropertyCard.SaveArea"]': {
      marginRight: 'none',
      marginTop: 'none',
    },
  },
  variants: {
    loading: {
      true: {
        '& [data-c11n-component="PropertyCard.DataWrapper"]': {
          // eslint-disable-next-line hotpads/no-magic-numbers -- C11n has no public token or prop for the resolved card data height
          minBlockSize: '92px',
        },
      },
    },
  },
});

export const largeCardOverrides = largeCardOverridesRecipe.raw();
export const largeCardSkeletonOverrides = largeCardOverridesRecipe.raw({ loading: true });

const compactCardOverridesRecipe = cva({
  base: {
    ...cardOverrides,
    '& [data-c11n-component="PropertyCard.Body"]': {
      borderRadius: 'md',
      // Photo + data rows size to content (see largeCardOverrides note).
      gridTemplateRows: 'auto auto',
    },
    '& [data-c11n-component="PropertyCard.PhotoWrapper"]': {
      aspectRatio: '4 / 3',
      minBlockSize: 'auto',
      overflow: 'hidden',
      borderRadius: 'md',
    },
    '& [data-c11n-component="PropertyCard.PhotoBody"]': {
      borderRadius: 'md',
    },
    '& [data-c11n-component="PhotoCarousel.Root"]': {
      borderRadius: 'md',
      height: '100%',
    },
    '& [data-c11n-component="PhotoCarousel.Slide"] img': slideZoomResting,
    '& [data-c11n-component="PhotoCarousel.Root"] > [data-c11n-component="PhotoCarousel.NavControls"]': {
      display: 'none',
      opacity: 0,
      pointerEvents: 'none',
      zIndex: 'zIndexStepOver',
      lg: {
        display: 'flex',
      },
    },
    '& [data-c11n-component="PhotoCarousel.PreviousButton"]': {
      ...compactCarouselButtonBase,
      insetInlineEnd: 'auto',
      insetInlineStart: 'tightest',
    },
    '& [data-c11n-component="PhotoCarousel.NextButton"]': {
      ...compactCarouselButtonBase,
      insetInlineEnd: 'tightest',
      insetInlineStart: 'auto',
    },
    '& [data-c11n-component="PhotoCarousel.PreviousButton"]:disabled': {
      opacity: 0,
      pointerEvents: 'none',
    },
    '& [data-c11n-component="PhotoCarousel.NextButton"]:disabled': {
      opacity: 0,
      pointerEvents: 'none',
    },
    '& [data-c11n-component="PhotoCarousel.PreviousButton"]:is(:active, :hover, [data-active], [data-hover]), & [data-c11n-component="PhotoCarousel.NextButton"]:is(:active, :hover, [data-active], [data-hover])':
      carouselButtonInteractiveState,
    '& [data-c11n-component="PhotoCarousel.Dots"]': {
      backgroundColor: 'transparent.invisible',
      gap: 'tightest',
      insetBlockEnd: '150',
      insetInlineEnd: 0,
      insetInlineStart: 0,
      justifyContent: 'center',
      minHeight: 'auto',
      paddingBlock: 0,
      paddingInline: 0,
      transform: 'none',
      width: '100%',
      zIndex: 'zIndexStepOver',
      _focusWithin: {
        backgroundColor: 'transparent.invisible',
      },
    },
    '& [data-c11n-component="PhotoCarousel.Dot"]': {
      backgroundColor: 'transparent.white-40',
      borderRadius: 'ellipse',
      height: '100',
      width: '100',
    },
    '& [data-c11n-component="PhotoCarousel.Dot"][aria-selected="true"]': {
      backgroundColor: 'white',
      height: '100',
      width: '100',
    },
    _hoverMouse: {
      '& [data-c11n-component="PhotoCarousel.Root"]:hover [data-c11n-component="PhotoCarousel.Slide"] img':
        slideZoomHover,
      '& [data-c11n-component="PhotoCarousel.Root"]:hover > [data-c11n-component="PhotoCarousel.NavControls"]': {
        opacity: 1,
        pointerEvents: 'auto',
      },
      '& [data-c11n-component="PhotoCarousel.Root"]:focus-within > [data-c11n-component="PhotoCarousel.NavControls"]': {
        opacity: 1,
        pointerEvents: 'auto',
      },
    },
    '& [data-c11n-component="PropertyCard.DataWrapper"]': {
      marginBlockStart: 'tightest',
      paddingInline: 'tighter',
      paddingBlockStart: 'tightest',
      paddingBlockEnd: 'tightest',
    },
    '& [data-c11n-component="PropertyCard.DataArea"]': {
      marginBlockEnd: 0,
      minWidth: 0,
    },
    '& [data-c11n-component="PropertyCard.SaveArea"]': {
      marginRight: 'none',
      marginTop: 'none',
    },
  },
});

export const compactCardOverrides = compactCardOverridesRecipe.raw();

/**
 * Horizontal appearance leans on Constellation's own photo-left / data-right
 * layout (proportional photo column, built-in data padding) instead of the
 * SRP-tuned overrides the vertical cards use — those made it cramped. Only the
 * read-only Share preview uses this today, so it's deliberately minimal: full
 * width + rounded corners, with the data padding restored (the HotPads theme
 * zeroes DataWrapper horizontal padding for the flush vertical cards).
 */
export const horizontalCardOverrides = css.raw({
  width: '100%',
  maxWidth: 'none',
  minWidth: 0,
  '& [data-c11n-component="PropertyCard.Body"]': {
    borderRadius: 'md',
    overflow: 'hidden',
  },
  // Square photo with all four corners rounded (Constellation uses a stretched,
  // left-rounded photo by default in the horizontal layout).
  '& [data-c11n-component="PropertyCard.PhotoWrapper"]': {
    alignSelf: 'center',
    aspectRatio: '1 / 1',
    borderRadius: 'md',
    minBlockSize: 'auto',
    overflow: 'hidden',
  },
  '& [data-c11n-component="PropertyCard.DataWrapper"]': {
    alignContent: 'center',
    paddingBlock: 0,
    paddingInline: 'default',
  },
  '& [data-c11n-component="PropertyCard.SaveArea"]': {
    marginRight: 'none',
    marginTop: 'none',
  },
});
